// OptimizationSystem.js
// Provides optimization techniques for the island map system including LOD and culling

import * as THREE from 'three';

/**
 * OptimizationSystem - Manages optimization techniques for the island map
 * including LOD (Level of Detail), frustum culling, and performance monitoring
 */
class OptimizationSystem {
  /**
   * Constructor for optimization system
   * @param {Object} options - Configuration options
   */
  constructor(options = {}) {
    this.options = {
      enableLOD: true,
      enableFrustumCulling: true,
      enableOcclusionCulling: false, // New option for occlusion culling
      enablePerformanceAdaptation: true,
      lodLevels: 4,
      lodNear: 10,
      lodFar: 100,
      frustumCullingInterval: 500, // ms between frustum checks
      performanceMonitoringInterval: 1000, // ms between performance checks
      ...options
    };

    // Performance tracking
    this.fps = 60;
    this.frameCount = 0;
    this.lastPerformanceCheck = 0;
    this.performanceTrend = 0; // -1: degrading, 0: stable, 1: improving
    this.qualityLevel = 3; // 0: low, 1: medium, 2: high, 3: ultra
    this.stableHighPerformanceFrames = 0; // Track frames with good performance
    
    // Auto-detect device capabilities and set initial quality
    this.detectHardwareCapabilities();
    
    // Frustum and occlusion culling
    this.frustum = new THREE.Frustum();
    this.lastFrustumUpdate = 0;
    this.culledObjects = new Map();
    this.occludedObjects = new Map(); // Track occluded objects
    this.frustumCullingBatchIndex = 0; // For batched processing
    
    // Camera tracking for culling optimization
    this.lastCameraPosition = null;
    this.lastCameraRotation = null;
    
    // Store LOD objects for management
    this.lodObjects = [];
    this.lodGeometryPool = new Map(); // Geometry cache for LOD levels
    
    // Make this object non-reactive if in a Vue context
    return typeof markRaw !== 'undefined' ? markRaw(this) : this;
  }
  
  /**
   * Detect hardware capabilities to set appropriate initial quality
   */
  detectHardwareCapabilities() {
    // Start with baseline assumptions
    let detectedQuality = 2; // Default to "high"
    
    // Check for mobile device
    const isMobile = typeof navigator !== 'undefined' && 
                    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    if (isMobile) {
      detectedQuality = 1; // Default to "medium" for mobile
    }
    
    // Check GPU via WebGL renderer info
    if (typeof document !== 'undefined') {
      try {
        const canvas = document.createElement('canvas');
        const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
        if (gl) {
          const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
          if (debugInfo) {
            const renderer = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL);
            console.log('Detected GPU:', renderer);
            
            // Lower quality for known low-end GPUs
            if (/Intel|HD Graphics|Iris|GMA/i.test(renderer)) {
              detectedQuality = Math.min(detectedQuality, 1);
            }
            
            // Higher quality for known high-end GPUs
            if (/RTX|GTX|Radeon Pro|AMD Radeon RX/i.test(renderer)) {
              detectedQuality = 3;
            }
          }
        }
      } catch (e) {
        console.warn('Failed to detect GPU capabilities:', e);
      }
    }
    
    // Set initial quality level based on detection
    this.qualityLevel = detectedQuality;
    console.log(`Hardware detection complete. Initial quality set to ${detectedQuality}`);
    
    // Apply the detected quality level
    this.applyQualityLevel();
  }
  
  /**
   * Apply Level of Detail to a terrain mesh
   * @param {THREE.Mesh} terrainMesh - Original full-detail terrain mesh
   * @param {THREE.Scene} scene - Scene to add LOD objects to
   * @returns {THREE.LOD} LOD object containing multiple detail levels
   */
  applyTerrainLOD(terrainMesh, scene) {
    if (!this.options.enableLOD) {
      return terrainMesh;
    }
    
    console.log('Creating terrain LOD with', this.options.lodLevels, 'levels');
    
    // Create LOD object
    const lod = new THREE.LOD();
    
    // Position the LOD object at the same position as the original mesh
    lod.position.copy(terrainMesh.position);
    
    // We don't copy the rotation to the LOD object yet
    // because we need each level to have the same intrinsic rotation
    
    // Add original mesh as highest detail level
    lod.addLevel(terrainMesh, 0);
    
    // Create lower detail levels
    for (let i = 1; i < this.options.lodLevels; i++) {
      // Calculate detail reduction factor
      const detailFactor = Math.pow(2, i);
      
      // Check if we have a cached simplified geometry for this detail level
      const cacheKey = `${terrainMesh.geometry.uuid}-${detailFactor}`;
      let simplifiedMesh;
      
      if (this.lodGeometryPool.has(cacheKey)) {
        // Use cached geometry
        const cachedGeometry = this.lodGeometryPool.get(cacheKey);
        simplifiedMesh = new THREE.Mesh(cachedGeometry, terrainMesh.material.clone());
        simplifiedMesh.rotation.copy(terrainMesh.rotation);
      } else {
        // Create and cache new simplified geometry
        simplifiedMesh = this.createSimplifiedTerrain(terrainMesh, detailFactor);
        this.lodGeometryPool.set(cacheKey, simplifiedMesh.geometry);
      }
      
      // Calculate distance for this level using exponential falloff
      const distance = this.options.lodNear * Math.pow(
        this.options.lodFar / this.options.lodNear,
        i / (this.options.lodLevels - 1)
      );
      
      // Add to LOD
      lod.addLevel(simplifiedMesh, distance);
    }
    
    // Add to scene in place of original mesh
    scene.add(lod);
    
    // Track for management
    this.lodObjects.push(lod);
    
    return lod;
  }
  
  /**
   * Remove a specific LOD object from the internal tracking array
   * @param {THREE.LOD} lodObject - The LOD object to remove
   */
  removeLODObject(lodObject) {
    this.lodObjects = this.lodObjects.filter(lod => lod !== lodObject);
    console.log('Removed LOD object from OptimizationSystem tracking.');
  }
  
  /**
   * Create a simplified version of the terrain mesh with fewer vertices
   * @param {THREE.Mesh} terrainMesh - Original terrain mesh
   * @param {number} simplificationFactor - Factor by which to reduce detail
   * @returns {THREE.Mesh} Simplified terrain mesh
   */
  createSimplifiedTerrain(terrainMesh, simplificationFactor) {
    // Get original geometry and material
    const originalGeometry = terrainMesh.geometry;
    const material = terrainMesh.material.clone();
    
    // Get parameters from original geometry
    const parameters = {
      width: originalGeometry.parameters.width,
      height: originalGeometry.parameters.height,
      widthSegments: Math.max(1, Math.floor(originalGeometry.parameters.widthSegments / simplificationFactor)),
      heightSegments: Math.max(1, Math.floor(originalGeometry.parameters.heightSegments / simplificationFactor))
    };
    
    console.log(`Creating simplified terrain with ${parameters.widthSegments} x ${parameters.heightSegments} segments`);
    
    // Create new geometry
    const simplifiedGeometry = new THREE.PlaneGeometry(
      parameters.width,
      parameters.height,
      parameters.widthSegments,
      parameters.heightSegments
    );
    
    // Create mesh
    const simplifiedMesh = new THREE.Mesh(simplifiedGeometry, material);
    
    // Sample heights from original geometry
    const originalPositions = originalGeometry.attributes.position;
    const originalWidth = originalGeometry.parameters.widthSegments + 1;
    const positions = simplifiedGeometry.attributes.position;
    
    // For each vertex in the simplified geometry
    for (let i = 0; i < positions.count; i++) {
      const x = positions.getX(i);
      const y = positions.getY(i);
      
      // Map to normalized coordinates (0 to 1)
      const normalizedX = (x + parameters.width / 2) / parameters.width;
      const normalizedY = (y + parameters.height / 2) / parameters.height;
      
      // Get index in original geometry
      const originalX = Math.floor(normalizedX * (originalWidth - 1));
      const originalY = Math.floor(normalizedY * (originalWidth - 1));
      const originalIndex = originalY * originalWidth + originalX;
      
      // Get height from original geometry (if valid)
      if (originalIndex < originalPositions.count) {
        const height = originalPositions.getZ(originalIndex);
        positions.setZ(i, height);
      }
    }
    
    // Update normals for proper lighting
    simplifiedGeometry.computeVertexNormals();
    
    // Copy properties from original mesh
    simplifiedMesh.castShadow = terrainMesh.castShadow;
    simplifiedMesh.receiveShadow = terrainMesh.receiveShadow;
    simplifiedMesh.userData = {...terrainMesh.userData};
    
    // Important: Copy the rotation from the original mesh to ensure consistent orientation
    // This is crucial for terrain which has a specific rotation to make it horizontal
    simplifiedMesh.rotation.copy(terrainMesh.rotation);
    
    return simplifiedMesh;
  }
  
  /**
   * Apply frustum culling to objects - optimized version with batched processing
   * @param {Array} objects - Array of objects to apply culling to
   * @param {THREE.Camera} camera - Camera to use for frustum calculation
   */
  applyFrustumCulling(objects, camera) {
    if (!this.options.enableFrustumCulling || !camera) {
      return;
    }
    
    const now = performance.now();
    
    // Skip if not enough time has passed since last update
    if (now - this.lastFrustumUpdate < this.options.frustumCullingInterval) {
      return;
    }
    
    this.lastFrustumUpdate = now;
    
    // Update frustum only when camera moves significantly
    const cameraPosition = camera.position.clone();
    const cameraRotation = camera.rotation.clone();
    
    if (!this.lastCameraPosition) {
      this.lastCameraPosition = cameraPosition;
      this.lastCameraRotation = cameraRotation;
    }
    
    // Check if camera moved enough to update frustum
    const positionChanged = this.lastCameraPosition.distanceTo(cameraPosition) > 1.0;
    const rotationChanged = Math.abs(this.lastCameraRotation.x - cameraRotation.x) > 0.1 ||
                            Math.abs(this.lastCameraRotation.y - cameraRotation.y) > 0.1;
    
    if (positionChanged || rotationChanged || !this.frustum) {
      // Update camera position/rotation cache
      this.lastCameraPosition.copy(cameraPosition);
      this.lastCameraRotation.copy(cameraRotation);
      
      // Update frustum
      this.frustum.setFromProjectionMatrix(
        new THREE.Matrix4().multiplyMatrices(
          camera.projectionMatrix,
          camera.matrixWorldInverse
        )
      );
    } else {
      // Camera hasn't moved enough, skip frustum update
      return;
    }
    
    // Process culling in batches to distribute workload
    const batchSize = 50; // Process 50 objects per frame
    const startIdx = this.frustumCullingBatchIndex || 0;
    let processed = 0;
    
    for (let i = 0; i < objects.length && processed < batchSize; i++) {
      const idx = (startIdx + i) % objects.length;
      const object = objects[idx];
      
      // Skip objects without a valid bounding sphere
      if (!object.geometry || !object.visible) continue;
      
      // Create or get the bounding sphere
      if (!object.geometry.boundingSphere) {
        object.geometry.computeBoundingSphere();
      }
      const boundingSphere = object.geometry.boundingSphere.clone();
      
      // Transform sphere to world space
      const center = boundingSphere.center.clone();
      center.applyMatrix4(object.matrixWorld);
      const radius = boundingSphere.radius * Math.max(
        object.scale.x,
        object.scale.y,
        object.scale.z
      );
      
      const worldBoundingSphere = new THREE.Sphere(center, radius);
      
      // Check if the object is in the frustum
      const isInFrustum = this.frustum.intersectsSphere(worldBoundingSphere);
      
      // Update visibility if needed
      if (this.culledObjects.get(object) && isInFrustum) {
        object.visible = true;
        this.culledObjects.delete(object);
      } else if (!this.culledObjects.get(object) && !isInFrustum) {
        object.visible = false;
        this.culledObjects.set(object, true);
      }
      
      processed++;
    }
    
    // Update batch index for next frame
    this.frustumCullingBatchIndex = (startIdx + processed) % objects.length;
  }
  
  /**
   * Apply occlusion culling to hide objects behind other objects
   * @param {Array} objects - Array of objects to apply culling to
   * @param {THREE.Camera} camera - Camera for occlusion testing
   */
  applyOcclusionCulling(objects, camera) {
    if (!this.options.enableOcclusionCulling) return;
    
    // Only update occlusion culling periodically
    const now = performance.now();
    if (now - this.lastOcclusionUpdate < 1000) return; // Once per second is enough
    
    this.lastOcclusionUpdate = now;
    
    // Use raycasting from camera to test if objects are occluded by others
    const raycaster = new THREE.Raycaster();
    const cameraPosition = camera.position.clone();
    
    // Process a subset of objects each frame (similar to frustum culling batching)
    const batchSize = 10; // Smaller batch because occlusion is more expensive
    const startIdx = this.occlusionCullingBatchIndex || 0;
    let processed = 0;
    
    // Small objects and objects marked as non-occluders should not block vision
    const potentialOccluders = objects.filter(obj => {
      // Skip small objects as occluders
      if (obj.geometry && obj.geometry.boundingSphere) {
        const size = obj.geometry.boundingSphere.radius * Math.max(
          obj.scale.x, obj.scale.y, obj.scale.z
        );
        // Objects must be reasonably large to be considered as occluders
        return size > 5;
      }
      return false;
    });
    
    for (let i = 0; i < objects.length && processed < batchSize; i++) {
      const idx = (startIdx + i) % objects.length;
      const object = objects[idx];
      
      // Skip if object doesn't have geometry or is not visible
      if (!object.geometry || !object.visible || this.culledObjects.get(object)) continue;
      
      // Get object center in world space
      if (!object.geometry.boundingSphere) {
        object.geometry.computeBoundingSphere();
      }
      const objectBoundingSphere = object.geometry.boundingSphere.clone();
      const center = objectBoundingSphere.center.clone().applyMatrix4(object.matrixWorld);
      
      // Skip if object is too close to camera (wouldn't make sense to occlude)
      if (center.distanceTo(cameraPosition) < 5) {
        if (this.occludedObjects.has(object)) {
          object.visible = true;
          this.occludedObjects.delete(object);
        }
        continue;
      }
      
      // Create direction vector from camera to object
      const direction = center.clone().sub(cameraPosition).normalize();
      raycaster.set(cameraPosition, direction);
      
      // Set near to a small value and far to slightly less than distance to object
      const distance = cameraPosition.distanceTo(center);
      raycaster.near = 1;  // Skip objects very close to camera
      raycaster.far = distance * 0.95;
      
      // Test for intersections with potential occluders
      const intersects = raycaster.intersectObjects(potentialOccluders, false);
      
      // If something else is intersected, this object is occluded
      if (intersects.length > 0) {
        // Only hide if not already hidden
        if (!this.occludedObjects.has(object)) {
          object.visible = false;
          this.occludedObjects.set(object, true);
        }
      } else if (this.occludedObjects.has(object)) {
        // Make visible again if no longer occluded
        object.visible = true;
        this.occludedObjects.delete(object);
      }
      
      processed++;
    }
    
    // Update batch index for next frame
    this.occlusionCullingBatchIndex = (startIdx + processed) % objects.length;
  }
  
  /**
   * Monitor performance and adjust quality levels if needed
   * @param {number} deltaTime - Time since last frame in seconds
   */
  monitorPerformance(deltaTime) {
    if (!this.options.enablePerformanceAdaptation) {
      return;
    }
    
    // Count the frame
    this.frameCount++;
    
    const now = performance.now();
    
    // Calculate FPS every second
    if (now - this.lastPerformanceCheck >= this.options.performanceMonitoringInterval) {
      const elapsedTime = (now - this.lastPerformanceCheck) / 1000;
      this.fps = this.frameCount / elapsedTime;
      this.frameCount = 0;
      this.lastPerformanceCheck = now;
      
      // Determine performance trend
      if (this.fps < 30) {
        this.performanceTrend = -1; // Degrading
      } else if (this.fps > 55) {
        this.performanceTrend = 1; // Improving
      } else {
        this.performanceTrend = 0; // Stable
      }
      
      // Adjust quality if needed
      this.adjustQualityBasedOnPerformance();
    }
  }
  
  /**
   * Adjust quality level based on performance with hysteresis
   */
  adjustQualityBasedOnPerformance() {
    // If performance is poor, decrease quality immediately
    if (this.performanceTrend === -1 && this.qualityLevel > 0) {
      this.qualityLevel--;
      this.stableHighPerformanceFrames = 0; // Reset stability counter
      this.applyQualityLevel();
      console.log(`Decreased quality to level ${this.qualityLevel} due to low FPS (${this.fps.toFixed(1)})`);
    } 
    // If performance is good for sustained period, increase quality
    else if (this.performanceTrend === 1 && this.qualityLevel < 3) {
      // Track frames with good performance
      this.stableHighPerformanceFrames += 1;
      
      // Only increase quality after sustained good performance (hysteresis)
      if (this.stableHighPerformanceFrames >= 5) { // About 5 seconds of good performance
        this.qualityLevel++;
        this.stableHighPerformanceFrames = 0; // Reset counter
        this.applyQualityLevel();
        console.log(`Increased quality to level ${this.qualityLevel} due to high sustained FPS (${this.fps.toFixed(1)})`);
      }
    } else if (this.performanceTrend !== 1) {
      // Reset stability counter if performance isn't great
      this.stableHighPerformanceFrames = 0;
    }
  }
  
  /**
   * Apply the current quality level settings
   */
  applyQualityLevel() {
    // Create quality change event details with more settings
    const qualitySettings = {
      qualityLevel: this.qualityLevel,
      lodLevels: Math.max(2, 4 - this.qualityLevel),
      lodNear: 15 + this.qualityLevel * 5,
      lodFar: 150 + this.qualityLevel * 50,
      grassDensity: 0.3 + this.qualityLevel * 0.2,
      treeDensity: 0.3 + this.qualityLevel * 0.2,
      bushDensity: 0.2 + this.qualityLevel * 0.2,
      enableOcclusionCulling: this.qualityLevel < 3, // Only on lower quality settings
      frustumCullingInterval: 500 - this.qualityLevel * 100, // More frequent at higher quality
      shadows: this.qualityLevel > 1 // Only enable shadows at medium or higher
    };
    
    // Only dispatch event if window is available (browser environment)
    if (typeof window !== 'undefined') {
      const event = new CustomEvent('island-quality-changed', {
        detail: qualitySettings
      });
      window.dispatchEvent(event);
    } else {
      // In Node.js environment (for testing), just log the change
      console.log('Quality changed:', qualitySettings);
    }
    
    // Update internal options to match new quality level
    this.options.lodLevels = qualitySettings.lodLevels;
    this.options.lodNear = qualitySettings.lodNear;
    this.options.lodFar = qualitySettings.lodFar;
    this.options.enableOcclusionCulling = qualitySettings.enableOcclusionCulling;
    this.options.frustumCullingInterval = qualitySettings.frustumCullingInterval;
  }
  
  /**
   * Update the optimization system
   * @param {number} deltaTime - Time in seconds since last update
   * @param {THREE.Camera} camera - Camera for frustum culling
   * @param {Array} objects - Array of objects to optimize
   */
  update(deltaTime, camera, objects) {
    // Apply frustum culling
    if (camera && objects && objects.length > 0) {
      this.applyFrustumCulling(objects, camera);
      
      // Apply occlusion culling if enabled
      if (this.options.enableOcclusionCulling) {
        this.applyOcclusionCulling(objects, camera);
      }
    }
    
    // Monitor performance
    this.monitorPerformance(deltaTime);
    
    // Update LOD objects
    if (camera) {
      for (const lod of this.lodObjects) {
        if (lod.update) {
          lod.update(camera);
        }
      }
    }
  }
  
  /**
   * Clean up resources
   */
  dispose() {
    // Dispose of LOD objects
    for (const lod of this.lodObjects) {
      lod.levels.forEach(level => {
        const object = level.object;
        if (object.geometry) {
          object.geometry.dispose();
        }
        if (object.material) {
          if (Array.isArray(object.material)) {
            object.material.forEach(material => material.dispose());
          } else {
            object.material.dispose();
          }
        }
      });
    }
    
    // Clear cached geometry in the LOD pool
    this.lodGeometryPool.forEach(geometry => {
      if (geometry && geometry.dispose) {
        geometry.dispose();
      }
    });
    this.lodGeometryPool.clear();
    
    // Clear arrays
    this.lodObjects = [];
    this.culledObjects.clear();
    this.occludedObjects.clear();
    
    // Clear camera tracking
    this.lastCameraPosition = null;
    this.lastCameraRotation = null;
  }
}

export default OptimizationSystem; 